import React, { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import { useContract } from '../hooks/useContract';
import { formatUSDT, formatAddress } from '../utils/constants';
import { fetchBlacklist, setBlacklistEntry } from '../utils/blacklist';
import './AdminPanel.css';

const AdminPanel = ({ account, provider, signer, network }) => {
  const { 
    contract,
    loading,
    adminRegister,
    adminUpgradePackage,
    adminRetopupPackage,
    getContractStats,
    getPoolAccumulations,
    getUserDetails,
    getUserPackages,
    getIncomeBreakdown
  } = useContract(provider, signer, network);

  // State for admin data
  const [adminLoading, setAdminLoading] = useState(false);
  const [totalUsers, setTotalUsers] = useState('0');
  const [reservePool, setReservePool] = useState('0');
  const [poolAccumulations, setPoolAccumulations] = useState({
    galaxy: '0',
    cosmos: '0', 
    universe: '0',
    eternal: '0'
  });
  
  // Pool eligible counts
  const [poolCounts, setPoolCounts] = useState({
    galaxy: '0',
    cosmos: '0',
    universe: '0', 
    eternal: '0'
  });

  // Form states
  const [adminUserAddress, setAdminUserAddress] = useState('');
  const [adminReferrerId, setAdminReferrerId] = useState('');
  const [adminUpgradeUserId, setAdminUpgradeUserId] = useState('');
  const [adminUpgradePackageId, setAdminUpgradePackageId] = useState('2');
  const [adminTopupUserId, setAdminTopupUserId] = useState('');
  const [adminTopupPackageId, setAdminTopupPackageId] = useState('1');
  const [poolAmount, setPoolAmount] = useState('');
  const [selectedPool, setSelectedPool] = useState('1');
  const [emergencyAmount, setEmergencyAmount] = useState('');
  const [searchUserId, setSearchUserId] = useState('');
  const [userDetails, setUserDetails] = useState(null);
  const [loadingUser, setLoadingUser] = useState(false);

  // Blacklist state
  const [blacklist, setBlacklist] = useState({ addresses: [], ids: [] });
  const [blacklistBusy, setBlacklistBusy] = useState(false);
  const [adminKey, setAdminKey] = useState(() => {
    try { return localStorage.getItem('blacklistAdminKey') || ''; } catch (_) { return ''; }
  });
  const [rememberKey, setRememberKey] = useState(() => {
    try { return !!localStorage.getItem('blacklistAdminKey'); } catch (_) { return false; }
  });
  const [blAddressInput, setBlAddressInput] = useState('');
  const [blIdInput, setBlIdInput] = useState('');

  // LOOP PREVENTION
  const hasLoadedRef = useRef(false);
  const isLoadingRef = useRef(false);

  // Load admin data when contract is available - PREVENT LOOPS
  useEffect(() => {
    // Prevent loops and duplicate calls
    if (!contract || hasLoadedRef.current || isLoadingRef.current) {
      return;
    }

    console.log('Loading admin data...');
    hasLoadedRef.current = true;
    loadAdminData();
  }, [contract]); // MINIMAL dependencies

  const loadAdminData = async () => {
    // Prevent concurrent calls
    if (isLoadingRef.current) return;
    
    isLoadingRef.current = true;
    setAdminLoading(true);
    
    try {
      // Load contract stats
      const stats = await getContractStats();
      if (stats) {
        setTotalUsers(stats.totalUsers);
        setReservePool(stats.reservePool);
      }
      
      // Load pool accumulations
      const pools = await getPoolAccumulations();
      if (pools) {
        setPoolAccumulations(pools);
      }
      
      // Load pool eligible counts
      if (contract.getPoolEligibleCounts) {
        try {
          const counts = await contract.getPoolEligibleCounts();
          setPoolCounts({
            galaxy: counts[0].toString(),
            cosmos: counts[1].toString(),
            universe: counts[2].toString(),
            eternal: counts[3].toString()
          });
        } catch (error) {
          console.warn('Failed to load pool counts:', error);
        }
      }
      
      console.log('Admin data loaded successfully');
      
    } catch (error) {
      console.error('Error loading admin data:', error);
      alert('Failed to load some data. Please refresh.');
    } finally {
      setAdminLoading(false);
      isLoadingRef.current = false;
    }
  };

  // Manual refresh function
  const handleRefreshData = () => {
    hasLoadedRef.current = false;
    isLoadingRef.current = false;
    loadAdminData();
  };

  // --- Blacklist helpers ---
  const refreshBlacklist = async () => {
    try {
      const data = await fetchBlacklist();
      setBlacklist(data);
    } catch (err) {
      console.error('refreshBlacklist failed:', err);
    }
  };

  useEffect(() => {
    refreshBlacklist();
  }, []);

  const persistKey = (key, remember) => {
    try {
      if (remember && key) {
        localStorage.setItem('blacklistAdminKey', key);
      } else {
        localStorage.removeItem('blacklistAdminKey');
      }
    } catch (_) { /* ignore */ }
  };

  const runBlacklistOp = async (action, type, value) => {
    if (!adminKey) {
      alert('Enter the admin key first.');
      return;
    }
    if (!value) return;
    try {
      setBlacklistBusy(true);
      const result = await setBlacklistEntry({ action, type, value, adminKey });
      if (result && Array.isArray(result.addresses) && Array.isArray(result.ids)) {
        setBlacklist({
          addresses: result.addresses.map((a) => String(a).toLowerCase()),
          ids: result.ids.map((v) => String(v)),
        });
      } else {
        await refreshBlacklist();
      }
      persistKey(adminKey, rememberKey);
    } catch (err) {
      alert(`Blacklist ${action} failed: ${err.message}`);
    } finally {
      setBlacklistBusy(false);
    }
  };

  const handleBlacklistAddAddress = () => {
    const val = blAddressInput.trim();
    if (!/^0x[a-fA-F0-9]{40}$/.test(val)) {
      alert('Invalid address. Must be 0x + 40 hex chars.');
      return;
    }
    runBlacklistOp('add', 'address', val).then(() => setBlAddressInput(''));
  };

  const handleBlacklistAddId = () => {
    const val = blIdInput.trim();
    if (!/^\d+$/.test(val)) {
      alert('Invalid user ID. Must be digits only.');
      return;
    }
    runBlacklistOp('add', 'id', val).then(() => setBlIdInput(''));
  };

  const handleBlacklistRemoveAddress = (addr) => {
    if (!window.confirm(`Remove ${addr} from blacklist?`)) return;
    runBlacklistOp('remove', 'address', addr);
  };

  const handleBlacklistRemoveId = (id) => {
    if (!window.confirm(`Remove user ID ${id} from blacklist?`)) return;
    runBlacklistOp('remove', 'id', id);
  };

  // Admin register
  const handleAdminRegister = async () => {
    if (!adminUserAddress) return;
    
    try {
      setAdminLoading(true);
      await adminRegister(adminUserAddress, adminReferrerId || 100000);
      alert('Registration successful!');
      setAdminUserAddress('');
      setAdminReferrerId('');
      // Manual refresh after 3 seconds
      setTimeout(() => {
        hasLoadedRef.current = false;
        loadAdminData();
      }, 3000);
    } catch (error) {
      console.error('Admin register failed:', error);
      alert(`Registration failed: ${error.message}`);
    } finally {
      setAdminLoading(false);
    }
  };

  // Admin upgrade
  const handleAdminUpgrade = async () => {
    if (!adminUpgradeUserId) return;
    
    try {
      setAdminLoading(true);
      await adminUpgradePackage(adminUpgradeUserId, adminUpgradePackageId);
      alert(`Upgrade to U${adminUpgradePackageId} successful!`);
      setAdminUpgradeUserId('');
      setTimeout(() => {
        hasLoadedRef.current = false;
        loadAdminData();
      }, 3000);
    } catch (error) {
      console.error('Admin upgrade failed:', error);
      alert(`Upgrade failed: ${error.message}`);
    } finally {
      setAdminLoading(false);
    }
  };

  // Admin topup
  const handleAdminTopup = async () => {
    if (!adminTopupUserId) return;
    
    try {
      setAdminLoading(true);
      await adminRetopupPackage(adminTopupUserId, adminTopupPackageId);
      alert(`Topup for U${adminTopupPackageId} successful!`);
      setAdminTopupUserId('');
      setTimeout(() => {
        hasLoadedRef.current = false;
        loadAdminData();
      }, 3000);
    } catch (error) {
      console.error('Admin topup failed:', error);
      alert(`Topup failed: ${error.message}`);
    } finally {
      setAdminLoading(false);
    }
  };

  // Set pool income - using contract with signer directly
  const handleSetPoolIncome = async () => {
    if (!poolAmount || !signer) return;
    
    try {
      setAdminLoading(true);
      
      // Create contract with signer for admin functions
      const contractWithSigner = new ethers.Contract(
        network?.chainId === '0x15EB' 
          ? '0xAfd8825Be638c8008004A71Fffe052F659458c55' 
          : '0x1F3896524dbD810Aa2ab096E8e585c7DcFa5e11B',
        [
          "function setGalaxyPoolIncome(uint256 amountPerUser) external",
          "function setCosmosPoolIncome(uint256 amountPerUser) external", 
          "function setUniversePoolIncome(uint256 amountPerUser) external",
          "function setEternalPoolIncome(uint256 amountPerUser) external"
        ],
        signer
      );
      
      const amount = ethers.utils.parseEther(poolAmount);
      
      let tx;
      if (selectedPool === '1') {
        tx = await contractWithSigner.setGalaxyPoolIncome(amount);
      } else if (selectedPool === '2') {
        tx = await contractWithSigner.setCosmosPoolIncome(amount);
      } else if (selectedPool === '3') {
        tx = await contractWithSigner.setUniversePoolIncome(amount);
      } else if (selectedPool === '4') {
        tx = await contractWithSigner.setEternalPoolIncome(amount);
      }
      
      await tx.wait();
      alert('Pool income set successfully!');
      setPoolAmount('');
      setTimeout(() => {
        hasLoadedRef.current = false;
        loadAdminData();
      }, 3000);
    } catch (error) {
      console.error('Set pool income failed:', error);
      alert(`Set pool income failed: ${error.message}`);
    } finally {
      setAdminLoading(false);
    }
  };

  // Reset functions
  const handleResetWeeklyPools = async () => {
    if (!signer || !window.confirm('Reset Galaxy and Cosmos pools?')) return;
    
    try {
      setAdminLoading(true);
      
      const contractWithSigner = new ethers.Contract(
        network?.chainId === '0x15EB' 
          ? '0xAfd8825Be638c8008004A71Fffe052F659458c55' 
          : '0x1F3896524dbD810Aa2ab096E8e585c7DcFa5e11B',
        ["function resetWeeklyPools() external"],
        signer
      );
      
      const tx = await contractWithSigner.resetWeeklyPools();
      await tx.wait();
      alert('Weekly pools reset successfully!');
      setTimeout(() => {
        hasLoadedRef.current = false;
        loadAdminData();
      }, 3000);
    } catch (error) {
      console.error('Reset weekly pools failed:', error);
      alert(`Reset failed: ${error.message}`);
    } finally {
      setAdminLoading(false);
    }
  };

  const handleResetMonthlyPools = async () => {
    if (!signer || !window.confirm('Reset Universe and Eternal pools?')) return;
    
    try {
      setAdminLoading(true);
      
      const contractWithSigner = new ethers.Contract(
        network?.chainId === '0x15EB' 
          ? '0xAfd8825Be638c8008004A71Fffe052F659458c55' 
          : '0x1F3896524dbD810Aa2ab096E8e585c7DcFa5e11B',
        ["function resetMonthlyPools() external"],
        signer
      );
      
      const tx = await contractWithSigner.resetMonthlyPools();
      await tx.wait();
      alert('Monthly pools reset successfully!');
      setTimeout(() => {
        hasLoadedRef.current = false;
        loadAdminData();
      }, 3000);
    } catch (error) {
      console.error('Reset monthly pools failed:', error);
      alert(`Reset failed: ${error.message}`);
    } finally {
      setAdminLoading(false);
    }
  };

  const handleResetAllPools = async () => {
    if (!signer || !window.confirm('Reset ALL pools?')) return;
    
    try {
      setAdminLoading(true);
      
      const contractWithSigner = new ethers.Contract(
        network?.chainId === '0x15EB' 
          ? '0xAfd8825Be638c8008004A71Fffe052F659458c55' 
          : '0x1F3896524dbD810Aa2ab096E8e585c7DcFa5e11B',
        ["function resetAllPoolAccumulations() external"],
        signer
      );
      
      const tx = await contractWithSigner.resetAllPoolAccumulations();
      await tx.wait();
      alert('All pools reset successfully!');
      setTimeout(() => {
        hasLoadedRef.current = false;
        loadAdminData();
      }, 3000);
    } catch (error) {
      console.error('Reset all pools failed:', error);
      alert(`Reset failed: ${error.message}`);
    } finally {
      setAdminLoading(false);
    }
  };

  const handleClaimReservePool = async () => {
    if (!signer) return;
    
    try {
      setAdminLoading(true);
      
      const contractWithSigner = new ethers.Contract(
        network?.chainId === '0x15EB' 
          ? '0xAfd8825Be638c8008004A71Fffe052F659458c55' 
          : '0x1F3896524dbD810Aa2ab096E8e585c7DcFa5e11B',
        ["function claimReservePool() external"],
        signer
      );
      
      const tx = await contractWithSigner.claimReservePool();
      await tx.wait();
      alert('Reserve pool claimed successfully!');
      setTimeout(() => {
        hasLoadedRef.current = false;
        loadAdminData();
      }, 3000);
    } catch (error) {
      console.error('Claim reserve pool failed:', error);
      alert(`Claim failed: ${error.message}`);
    } finally {
      setAdminLoading(false);
    }
  };

  const handleEmergencyWithdraw = async () => {
    if (!emergencyAmount || !signer || !window.confirm(`Withdraw ${emergencyAmount} USDT?`)) return;
    
    try {
      setAdminLoading(true);
      
      const contractWithSigner = new ethers.Contract(
        network?.chainId === '0x15EB' 
          ? '0xAfd8825Be638c8008004A71Fffe052F659458c55' 
          : '0x1F3896524dbD810Aa2ab096E8e585c7DcFa5e11B',
        ["function emergencyWithdraw(uint256 amount) external"],
        signer
      );
      
      const amount = ethers.utils.parseEther(emergencyAmount);
      const tx = await contractWithSigner.emergencyWithdraw(amount);
      await tx.wait();
      alert('Emergency withdraw successful!');
      setEmergencyAmount('');
      setTimeout(() => {
        hasLoadedRef.current = false;
        loadAdminData();
      }, 3000);
    } catch (error) {
      console.error('Emergency withdraw failed:', error);
      alert(`Withdraw failed: ${error.message}`);
    } finally {
      setAdminLoading(false);
    }
  };

  // Search user
  const handleSearchUser = async () => {
    if (!searchUserId) return;
    
    setLoadingUser(true);
    try {
      const userDetail = await getUserDetails(searchUserId);
      const packages = await getUserPackages(searchUserId);
      const income = await getIncomeBreakdown(searchUserId);

      const activePackages = packages.filter(pkg => pkg.isActive);

      setUserDetails({
        ...userDetail,
        ...income,
        activePackages: activePackages
      });
    } catch (error) {
      console.error('User search failed:', error);
      alert(`User not found: ${error.message}`);
      setUserDetails(null);
    } finally {
      setLoadingUser(false);
    }
  };

  const getPoolName = (poolId) => {
    const names = { '1': 'Galaxy', '2': 'Cosmos', '3': 'Universe', '4': 'Eternal' };
    return names[poolId] || 'Unknown';
  };

  const getPoolAccumulation = () => {
    const pools = { 
      '1': poolAccumulations.galaxy, 
      '2': poolAccumulations.cosmos, 
      '3': poolAccumulations.universe, 
      '4': poolAccumulations.eternal 
    };
    return pools[selectedPool] || '0';
  };

  const getPoolCount = () => {
    const counts = { 
      '1': poolCounts.galaxy, 
      '2': poolCounts.cosmos, 
      '3': poolCounts.universe, 
      '4': poolCounts.eternal 
    };
    return counts[selectedPool] || '0';
  };

  const isLoading = loading || adminLoading;

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <h2>Admin Panel</h2>
        <div className="admin-account">
          <span>Admin: {formatAddress(account)}</span>
        </div>
      </div>

      <div className="admin-sections">
        {/* System Statistics */}
        <div className="admin-section">
          <h3>System Statistics</h3>
          
          <button 
            onClick={handleRefreshData} 
            disabled={isLoading}
            className="admin-btn"
            style={{ marginBottom: '16px' }}
          >
            {isLoading ? 'Loading...' : 'Refresh Stats'}
          </button>
          
          <div className="stats-grid">
            <div className="stat-item">
              <label>Total Users</label>
              <span>{totalUsers}</span>
            </div>
            <div className="stat-item">
              <label>Reserve Pool</label>
              <span>{formatUSDT(reservePool)}</span>
            </div>
            <div className="stat-item">
              <label>Galaxy Pool</label>
              <span>{formatUSDT(poolAccumulations.galaxy)}</span>
              <small style={{ color: '#90caf9', fontSize: '0.8rem' }}>
                Eligible Users: {poolCounts.galaxy}
              </small>
            </div>
            <div className="stat-item">
              <label>Cosmos Pool</label>
              <span>{formatUSDT(poolAccumulations.cosmos)}</span>
              <small style={{ color: '#90caf9', fontSize: '0.8rem' }}>
                Eligible Users: {poolCounts.cosmos}
              </small>
            </div>
            <div className="stat-item">
              <label>Universe Pool</label>
              <span>{formatUSDT(poolAccumulations.universe)}</span>
              <small style={{ color: '#90caf9', fontSize: '0.8rem' }}>
                Eligible Users: {poolCounts.universe}
              </small>
            </div>
            <div className="stat-item">
              <label>Eternal Pool</label>
              <span>{formatUSDT(poolAccumulations.eternal)}</span>
              <small style={{ color: '#90caf9', fontSize: '0.8rem' }}>
                Eligible Users: {poolCounts.eternal}
              </small>
            </div>
          </div>
        </div>

        {/* User Search */}
        <div className="admin-section">
          <h3>User Lookup</h3>
          <div className="admin-panel-form">
            <div className="admin-form-group">
              <label className="admin-form-label">User ID</label>
              <div className="search-container">
                <input
                  type="text"
                  className="admin-form-input"
                  value={searchUserId}
                  onChange={(e) => setSearchUserId(e.target.value)}
                  placeholder="Enter User ID"
                />
                <button
                  className="admin-btn"
                  onClick={handleSearchUser}
                  disabled={loadingUser || !searchUserId}
                >
                  {loadingUser ? 'Searching...' : 'Search'}
                </button>
              </div>
            </div>
            {userDetails && (
              <div className="user-details-display">
                <h4>User Details - ID: {userDetails.id}</h4>
                <div className="user-details-grid">
                  <div className="user-detail-item">
                    <span className="user-detail-label">Account:</span>
                    <span className="user-detail-value">{formatAddress(userDetails.account)}</span>
                  </div>
                  <div className="user-detail-item">
                    <span className="user-detail-label">Referrer:</span>
                    <span className="user-detail-value">{userDetails.referrer}</span>
                  </div>
                  <div className="user-detail-item">
                    <span className="user-detail-label">Status:</span>
                    <span className={`user-detail-value ${userDetails.isActive ? 'status-active' : 'status-inactive'}`}>
                      {userDetails.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="user-detail-item">
                    <span className="user-detail-label">Direct Referrals:</span>
                    <span className="user-detail-value">{userDetails.totalDirectReferrals}</span>
                  </div>
                  <div className="user-detail-item">
                    <span className="user-detail-label">Claimable:</span>
                    <span className="user-detail-value">{formatUSDT(userDetails.claimableAmount)}</span>
                  </div>
                  <div className="user-detail-item">
                    <span className="user-detail-label">Total Claimed:</span>
                    <span className="user-detail-value">{formatUSDT(userDetails.totalClaimed)}</span>
                  </div>
                  <div className="user-detail-item">
                    <span className="user-detail-label">Total Income:</span>
                    <span className="user-detail-value">{formatUSDT(userDetails.totalIncome)}</span>
                  </div>
                </div>

                <h5>Active Packages</h5>
                <div className="active-packages">
                  {userDetails.activePackages.length > 0 ? userDetails.activePackages.map(pkg => (
                    <span key={pkg.id} className="package-badge">
                      U{pkg.id}
                    </span>
                  )) : <span>No active packages</span>}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Admin User Management */}
        <div className="admin-section">
          <h3>Admin User Management</h3>
          
          <div style={{ marginBottom: '24px' }}>
            <h4>Admin Registration</h4>
            <div className="admin-panel-form">
              <div className="admin-form-group">
                <label className="admin-form-label">User Address</label>
                <input
                  type="text"
                  className="admin-form-input"
                  value={adminUserAddress}
                  onChange={(e) => setAdminUserAddress(e.target.value)}
                  placeholder="0x..."
                />
              </div>
              <div className="admin-form-group">
                <label className="admin-form-label">Referrer ID (Optional)</label>
                <input
                  type="text"
                  className="admin-form-input"
                  value={adminReferrerId}
                  onChange={(e) => setAdminReferrerId(e.target.value)}
                  placeholder="Leave empty for default"
                />
              </div>
              <button
                className="admin-btn"
                onClick={handleAdminRegister}
                disabled={isLoading || !adminUserAddress}
              >
                {isLoading ? 'Registering...' : 'Admin Register User'}
              </button>
            </div>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <h4>Admin Upgrade</h4>
            <div className="admin-panel-form">
              <div className="admin-form-group">
                <label className="admin-form-label">User ID</label>
                <input
                  type="text"
                  className="admin-form-input"
                  value={adminUpgradeUserId}
                  onChange={(e) => setAdminUpgradeUserId(e.target.value)}
                  placeholder="User ID"
                />
              </div>
              <div className="admin-form-group">
                <label className="admin-form-label">Package</label>
                <select
                  className="admin-form-select"
                  value={adminUpgradePackageId}
                  onChange={(e) => setAdminUpgradePackageId(e.target.value)}
                >
                  <option value="2">U2 - 20 USDT</option>
                  <option value="3">U3 - 40 USDT</option>
                  <option value="4">U4 - 80 USDT</option>
                  <option value="5">U5 - 150 USDT</option>
                  <option value="6">U6 - 250 USDT</option>
                  <option value="7">U7 - 450 USDT</option>
                </select>
              </div>
              <button
                className="admin-btn"
                onClick={handleAdminUpgrade}
                disabled={isLoading || !adminUpgradeUserId}
              >
                {isLoading ? 'Upgrading...' : 'Admin Upgrade'}
              </button>
            </div>
          </div>

          <div>
            <h4>Admin Topup</h4>
            <div className="admin-panel-form">
              <div className="admin-form-group">
                <label className="admin-form-label">User ID</label>
                <input
                  type="text"
                  className="admin-form-input"
                  value={adminTopupUserId}
                  onChange={(e) => setAdminTopupUserId(e.target.value)}
                  placeholder="User ID"
                />
              </div>
              <div className="admin-form-group">
                <label className="admin-form-label">Package</label>
                <select
                  className="admin-form-select"
                  value={adminTopupPackageId}
                  onChange={(e) => setAdminTopupPackageId(e.target.value)}
                >
                  <option value="1">U1 - 10 USDT</option>
                  <option value="2">U2 - 20 USDT</option>
                  <option value="3">U3 - 40 USDT</option>
                  <option value="4">U4 - 80 USDT</option>
                  <option value="5">U5 - 150 USDT</option>
                  <option value="6">U6 - 250 USDT</option>
                  <option value="7">U7 - 450 USDT</option>
                </select>
              </div>
              <button
                className="admin-btn"
                onClick={handleAdminTopup}
                disabled={isLoading || !adminTopupUserId}
              >
                {isLoading ? 'Topping up...' : 'Admin Topup'}
              </button>
            </div>
          </div>
        </div>

        {/* Pool Management */}
        <div className="admin-section">
          <h3>Pool Management</h3>
          
          <div className="admin-panel-form">
            <div className="admin-form-group">
              <label className="admin-form-label">Select Pool</label>
              <select
                className="admin-form-select"
                value={selectedPool}
                onChange={(e) => setSelectedPool(e.target.value)}
              >
                <option value="1">Galaxy Pool</option>
                <option value="2">Cosmos Pool</option>
                <option value="3">Universe Pool</option>
                <option value="4">Eternal Pool</option>
              </select>
            </div>

            <div className="pool-info-display">
              <h4>{getPoolName(selectedPool)} Pool Information</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <div style={{ fontSize: '0.9rem', color: '#90caf9' }}>Current Accumulation</div>
                  <div className="pool-accumulation">
                    {formatUSDT(getPoolAccumulation())}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.9rem', color: '#90caf9' }}>Eligible Users</div>
                  <div className="pool-accumulation">
                    {getPoolCount()} Users
                  </div>
                </div>
              </div>
            </div>

            <div className="admin-form-group">
              <label className="admin-form-label">Amount per User (USDT)</label>
              <input
                type="number"
                step="0.01"
                className="admin-form-input"
                value={poolAmount}
                onChange={(e) => setPoolAmount(e.target.value)}
                placeholder="0.00"
              />
            </div>

            <div className="admin-actions">
              <button
                className="admin-btn"
                onClick={handleSetPoolIncome}
                disabled={isLoading || !poolAmount}
              >
                {isLoading ? 'Setting...' : `Set ${getPoolName(selectedPool)} Pool Income`}
              </button>
            </div>

            <div style={{ marginTop: '24px' }}>
              <h4>Pool Reset Options</h4>
              <div className="admin-actions">
                <button
                  className="admin-btn danger"
                  onClick={handleResetWeeklyPools}
                  disabled={isLoading}
                >
                  {isLoading ? 'Resetting...' : 'Reset Weekly Pools'}
                </button>
                <button
                  className="admin-btn danger"
                  onClick={handleResetMonthlyPools}
                  disabled={isLoading}
                >
                  {isLoading ? 'Resetting...' : 'Reset Monthly Pools'}
                </button>
                <button
                  className="admin-btn danger"
                  onClick={handleResetAllPools}
                  disabled={isLoading}
                >
                  {isLoading ? 'Resetting...' : 'Reset All Pools'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Reserve Pool Management */}
        <div className="admin-section">
          <h3>Reserve Pool Management</h3>
          <div className="pool-info-display">
            <h4>Current Reserve Pool: {formatUSDT(reservePool)}</h4>
          </div>
          <div className="admin-actions">
            <button
              className="admin-btn"
              onClick={handleClaimReservePool}
              disabled={isLoading}
            >
              {isLoading ? 'Claiming...' : 'Claim Reserve Pool'}
            </button>
          </div>
        </div>

        {/* Blacklist Management */}
        <div className="admin-section">
          <h3>Blacklist Management</h3>
          <p style={{ color: '#90caf9', fontSize: '0.9rem', marginTop: 0 }}>
            Blacklisted wallet addresses and user IDs are blocked from logging in.
            The list is stored server-side (blacklist.json) via PHP; changes take
            effect immediately for anyone who reloads.
          </p>

          <div className="admin-panel-form">
            <div className="admin-form-group">
              <label className="admin-form-label">Admin Key</label>
              <input
                type="password"
                className="admin-form-input"
                value={adminKey}
                onChange={(e) => setAdminKey(e.target.value)}
                placeholder="Server secret from blacklist_set.php"
                autoComplete="off"
              />
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', fontSize: '0.85rem' }}>
                <input
                  type="checkbox"
                  checked={rememberKey}
                  onChange={(e) => {
                    setRememberKey(e.target.checked);
                    persistKey(adminKey, e.target.checked);
                  }}
                />
                Remember key on this device
              </label>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginTop: '16px' }}>
              {/* Addresses */}
              <div>
                <h4>Blacklisted Addresses ({blacklist.addresses.length})</h4>
                <div className="admin-form-group">
                  <div className="search-container">
                    <input
                      type="text"
                      className="admin-form-input"
                      value={blAddressInput}
                      onChange={(e) => setBlAddressInput(e.target.value)}
                      placeholder="0x..."
                    />
                    <button
                      className="admin-btn"
                      onClick={handleBlacklistAddAddress}
                      disabled={blacklistBusy || !blAddressInput}
                    >
                      {blacklistBusy ? '...' : 'Add'}
                    </button>
                  </div>
                </div>
                <div style={{
                  maxHeight: '220px',
                  overflowY: 'auto',
                  border: '1px solid rgba(100, 181, 246, 0.2)',
                  borderRadius: '8px',
                  padding: '8px'
                }}>
                  {blacklist.addresses.length === 0 ? (
                    <div style={{ color: '#90caf9', fontSize: '0.9rem' }}>No addresses blacklisted.</div>
                  ) : blacklist.addresses.map((addr) => (
                    <div key={addr} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '6px 8px',
                      borderBottom: '1px solid rgba(100,181,246,0.1)'
                    }}>
                      <span style={{ fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-all' }}>
                        {addr}
                      </span>
                      <button
                        className="admin-btn danger"
                        style={{ padding: '4px 10px', fontSize: '0.8rem' }}
                        onClick={() => handleBlacklistRemoveAddress(addr)}
                        disabled={blacklistBusy}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* IDs */}
              <div>
                <h4>Blacklisted User IDs ({blacklist.ids.length})</h4>
                <div className="admin-form-group">
                  <div className="search-container">
                    <input
                      type="text"
                      className="admin-form-input"
                      value={blIdInput}
                      onChange={(e) => setBlIdInput(e.target.value)}
                      placeholder="User ID (numeric)"
                    />
                    <button
                      className="admin-btn"
                      onClick={handleBlacklistAddId}
                      disabled={blacklistBusy || !blIdInput}
                    >
                      {blacklistBusy ? '...' : 'Add'}
                    </button>
                  </div>
                </div>
                <div style={{
                  maxHeight: '220px',
                  overflowY: 'auto',
                  border: '1px solid rgba(100, 181, 246, 0.2)',
                  borderRadius: '8px',
                  padding: '8px'
                }}>
                  {blacklist.ids.length === 0 ? (
                    <div style={{ color: '#90caf9', fontSize: '0.9rem' }}>No user IDs blacklisted.</div>
                  ) : blacklist.ids.map((id) => (
                    <div key={id} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '6px 8px',
                      borderBottom: '1px solid rgba(100,181,246,0.1)'
                    }}>
                      <span style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>#{id}</span>
                      <button
                        className="admin-btn danger"
                        style={{ padding: '4px 10px', fontSize: '0.8rem' }}
                        onClick={() => handleBlacklistRemoveId(id)}
                        disabled={blacklistBusy}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="admin-actions" style={{ marginTop: '16px' }}>
              <button
                className="admin-btn"
                onClick={refreshBlacklist}
                disabled={blacklistBusy}
              >
                Refresh Blacklist
              </button>
            </div>
          </div>
        </div>

        {/* Emergency Functions */}
        <div className="admin-section">
          <h3>Emergency Functions</h3>
          <div className="admin-panel-form">
            <div className="admin-form-group">
              <label className="admin-form-label">Emergency Withdraw Amount (USDT)</label>
              <input
                type="number"
                step="0.01"
                className="admin-form-input"
                value={emergencyAmount}
                onChange={(e) => setEmergencyAmount(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div className="admin-actions">
              <button
                className="admin-btn danger"
                onClick={handleEmergencyWithdraw}
                disabled={isLoading || !emergencyAmount}
              >
                {isLoading ? 'Withdrawing...' : 'Emergency Withdraw'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;